import type { SearchFreelancersResponse } from "../types";

/**
 * Stampa su ogni freelancer il `search_id` dell'envelope della **propria**
 * pagina (attribuzione per-pagina, D2 della spec `search-id-forwarding`).
 *
 * La lista di ricerca è incrementale ("Carica altri"): un'azione su una card
 * di pagina N deve portare l'id della pagina N, non quello dell'ultima pagina
 * ricevuta — altrimenti l'esito cadrebbe dai join per-impression del BE.
 *
 * Da qui in poi l'id viaggia come **dato**: i flatten esistenti, lo snapshot
 * `lastGood`, la sheet e lo store di selezione lo ereditano senza modifiche.
 * Un `search_id: null` nell'envelope (log server fallito) non produce il campo:
 * la pagina torna **per riferimento**, quindi la chiave non nasce affatto e
 * sopravvive a un `in` o a uno spread.
 *
 * Va chiamata nella `queryFn`, una volta per pagina appena arrivata — **non**
 * in una `select`. `@tanstack/solid-query` forza `structuralSharing = false`
 * (`useBaseQuery.ts`), quindi una `select` che ricostruisce gli oggetti li
 * consegna nuovi a ogni cambio di `data`: al primo "Carica altri" il `<For>`
 * del pannello butterebbe e ricostruirebbe tutte le righe già a schermo (con il
 * loro stato: popover aperti, hover, focus) invece di appendere le nuove.
 * Stampando qui, `infiniteQueryBehavior` riusa gli oggetti-pagina precedenti e
 * l'identità resta stabile.
 */
export const stampSearchPage = (
  page: SearchFreelancersResponse,
): SearchFreelancersResponse => {
  // Const locale, non `page.search_id` inline: il narrowing non sopravvive alla
  // closure del `map`, e un `as string` mentirebbe in silenzio se il contratto
  // dell'envelope cambiasse tipo.
  const searchId = page.search_id;
  if (!searchId) return page;
  return {
    ...page,
    data: page.data.map((freelancer) => ({
      ...freelancer,
      search_id: searchId,
    })),
  };
};
